import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnChanges,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { PropsOf } from '@blaze-runtime/engine-domain';
import { ConnectorInfo, ConnectorTarget } from '@blaze-shared/api-contracts-domain';
import {
  accountSelectors,
  blazeActions,
  projectContextSelectors,
} from '@blaze-shared/app-state-data-access';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
  ConnectorsTemplatesCollectionService,
} from '@blaze-shared/connectors-data-access';
import {
  ConnectorConfiguration,
  connectorServiceIcons,
  ConnectorTemplate,
  GoogleServiceType,
} from '@blaze-shared/connectors-domain';
import { getBackground, makeFontStyles, makeFontStyleStyles } from '@blaze-shared/elements-domain';
import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { SelectComponent } from '@blaze-shared/shared-feature';
import { safeDetectChanges, TooltipDirective } from '@blaze-shared/shared-ui';
import { isDeepEqual, isNullish, SelectOption } from '@blaze-shared/shared-util';

import { CanvasElementBaseComponent } from '../__base/base.component';

import { ConnectionSelectorService } from './connection-selector.service';

@UntilDestroy()
@Component({
  selector: 'be-connection-selector',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    SelectComponent,
    TooltipDirective,
  ],
  templateUrl: './connection-selector.component.html',
  styleUrls: ['./connection-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConnectionSelectorService],
})
export class ConnectionSelectorComponent
  extends CanvasElementBaseComponent<'connectionSelector'>
  implements OnChanges, OnInit
{
  private readonly connectionSelectorService = inject(ConnectionSelectorService);
  private readonly connectorsApiService = inject(ConnectorsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly connectorTemplatesCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);

  private authState?: string;
  private connectorUpdate$ = new BehaviorSubject<string>('');
  private projectId?: string;
  private selectorId?: string;
  private readonly _props = signal<PropsOf<'connectionSelector'> | undefined>(undefined);
  private readonly connectionConnectors = signal<
    ConnectorConfiguration<Record<string, any>>[] | undefined
  >(undefined);
  private readonly connectorSelector = signal<ConnectorInfo | undefined>(undefined);
  // Connector used to select user-specific connectors (base connector)
  private readonly connectorTemplate = signal<ConnectorTemplate | null | undefined>(undefined);
  private readonly isCustomerPortal = signal<boolean>(true); // for backwards compatibility default to true

  protected availableCalendars = signal<SelectOption<string>[] | undefined>(undefined);
  protected buttonTooltip: Signal<string>;
  protected connection = signal<ConnectorInfo | null>(null); // created connection
  protected connectorIcon = signal<string>('');
  protected connectorLabel = signal(''); // label from props
  protected containerStyles!: Record<string, string>;
  protected form = new FormGroup({
    calendarId: new FormControl<string>(''),
  });
  protected hasError = signal(false);
  protected isButtonDisabled: Signal<boolean>;
  protected isConnected: Signal<boolean>;
  protected isConnectorIconVisible = signal(false);
  protected isEditable: Signal<boolean>;
  protected isGoogleCalendarSelector: Signal<boolean>;
  protected isGoogleMailSelector: Signal<boolean>;
  protected label: Signal<string>;
  protected primaryButtonStyles!: Record<string, string>;
  protected secondaryButtonStyles!: Record<string, string>;

  constructor() {
    super();
    this.actions$
      .pipe(untilDestroyed(this), ofType(blazeActions.createConnectorError))
      .subscribe(() => {
        this.hasError.set(true);
      });
    this.initSignals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      this._props.set(changes['props'].currentValue);
      const content = (this.props?.content ?? {}) as any;
      const selectorConfig = content['connectionSelector'];

      this.isConnectorIconVisible.set(!selectorConfig?.['hideIcon']);
      this.connectorLabel.set(selectorConfig?.['connectorLabel'] || '');
      this.selectorId = selectorConfig?.['selectorId'] || '';
      this.connectorUpdate$.next(this.selectorId || '');
    }
  }
  ngOnInit(): void {
    this.initConnectorSelector();
  }

  emitClick(toolbarTriggered = false): void {
    if (!this.isInEditor || toolbarTriggered) {
      this.output.emit({
        eventType: 'click',
        data: undefined,
        isInitialData: false,
        schema: null,
      });
    }
  }

  /**
   * Creates new connection based on the configured connector selector.
   */
  protected onConnect(): Subscription {
    this.hasError.set(false);

    return combineLatest({
      projectId: this.store.select(runtimeSelectors.selectProjectId),
      externalAccountRowId: this.store.select(projectContextSelectors.selectExternalAccountRowId),
      templates: this.connectorTemplatesCollService.entities$,
    })
      .pipe(
        take(1),
        map(({ projectId, templates }) => {
          const template = templates.find(
            (tpl) => tpl.target === this.connectorSelector()?.configuration?.target,
          );
          return { projectId, template };
        }),
        filter(({ projectId, template }) => !!projectId && !!template),
      )
      .subscribe(({ projectId, template }) => {
        this.connectorTemplate.set(template);

        // Google Calendar
        if (template?.target === 'GoogleCalendar') {
          this.createGoogleCalendarOptions();
          return;
        }

        // GMail
        if (template?.target === 'GoogleMail') {
          this.createGoogleMailConnection();
          return;
        }

        // Other connectors
        // Mark in local storage the flag which will help determine whether externalAccountRowId and projectId
        // should be included when creating the connector. This is necessary because some connectors when they are created
        // open in a new window.
        this.localStorageService.set(StorageKey.IsExternalAccountConnector, true);
        this.store.dispatch(blazeActions.createConnector({ projectId, template }));
      });
  }

  /**
   * Creates connection with specified Google Calendar.
   */
  protected onConnectGoogleCalendar(): void {
    const connectorTemplate = this.connectorTemplate();

    if (!this.authState) {
      this.notificationService.error(
        'Unable to connect calendar. Please authorise your Google account',
      );
      return;
    }
    if (!this.form.value.calendarId) {
      this.notificationService.error('Please select a calendar');
      return;
    }
    if (!connectorTemplate?.id) {
      this.notificationService.error('Please select a connector');
      return;
    }

    const calendarName = this.availableCalendars()?.find(
      (calendar) => calendar.value === this.form.value.calendarId,
    )?.label;
    this.connectionSelectorService
      .createGoogleCalendarConnector(
        this.authState,
        this.form.value.calendarId,
        calendarName || this.form.value.calendarId,
        connectorTemplate.id,
      )
      .subscribe((connection: ConnectorInfo | null) => {
        this.connection.set(connection);
        // Calendar has been chosen. Clear the list to show configured connection.
        this.availableCalendars.set(undefined);
      });
  }

  protected onEditConnection(): void {
    const connector = this.connection();
    if (!connector) {
      return;
    }
    const connectorConfig: ConnectorConfiguration = {
      ...connector,
      projectId: this.projectId,
    };
    this.store.dispatch(blazeActions.editConnector({ connector: connectorConfig }));
  }

  protected onRemoveConnection(): void {
    const connectorId = this.connection()?.id;
    if (connectorId) {
      this.connectorsCollService.delete(connectorId).subscribe((deletedConnectorId) => {
        if (!!deletedConnectorId && deletedConnectorId === connectorId) {
          this.connection.set(null);
          this.form.reset();
          this.availableCalendars.set(undefined);
        }
      });
    }
  }

  /**
   * Fetches and creates calendar options for the Google Calendar connector.
   */
  private createGoogleCalendarOptions(): void {
    this.connectionSelectorService
      .connectOAuth('google', { googleServiceType: GoogleServiceType.Calendar })
      .pipe(
        switchMap(({ state }) => {
          this.authState = state;
          return this.connectionSelectorService.loadCalendars(state);
        }),
      )
      .subscribe((calendarOptions) => {
        this.availableCalendars.set(calendarOptions);
      });
  }

  /**
   * Crates GMail connection for authorized GMail account.
   */
  private createGoogleMailConnection(): void {
    const connectorTemplate = this.connectorTemplate();
    if (!connectorTemplate?.id) {
      this.notificationService.error('Please select a connector');
      return;
    }
    this.connectionSelectorService
      .connectOAuth('google', { googleServiceType: GoogleServiceType.Mail })
      .pipe(
        switchMap(({ state }) => {
          this.authState = state;
          return this.connectionSelectorService.createGoogleMailConnector(
            state,
            connectorTemplate.id,
          );
        }),
      )
      .subscribe((connection: ConnectorInfo | null) => {
        this.connection.set(connection);
      });
  }

  private initConnectorSelector(): void {
    combineLatest({
      // Used to auto-update component when a new connector when a user creates a new connector via Connect.
      accountId: this.store.select(accountSelectors.selectAccountId),
      connectors: this.connectorsCollService.entities$,
      connectorUpdate: this.connectorUpdate$.pipe(startWith('')),
      externalAccountRowId: this.store.select(projectContextSelectors.selectExternalAccountRowId),
      isCustomerPortal: this.store
        .select(runtimeSelectors.selectIsCustomerPortal)
        .pipe(filter((isCp) => !isNullish(isCp))),
      projectId: this.store.select(runtimeSelectors.selectProjectId),
      templates: this.connectorTemplatesCollService.entities$,
      userId: this.projectContextFacade.userId$,
    })
      .pipe(
        distinctUntilChanged(isDeepEqual),
        debounceTime(300),
        filter(({ projectId }) => !!projectId),
        map((info) => ({
          ...info,
          externalAccountRowId:
            info?.externalAccountRowId || this.projectContextFacade?.varAccount?._id || '',
        })),
        // Fetch templtes and connectors if necessary
        tap(
          ({
            accountId,
            connectors,
            externalAccountRowId,
            isCustomerPortal,
            projectId,
            templates,
          }) => {
            this.projectId = projectId;
            this.isCustomerPortal.set(isCustomerPortal);
            if (!templates.length) {
              this.connectorTemplatesCollService.getAll();
            }
            if (!connectors?.length && !externalAccountRowId && accountId) {
              this.schemaService.getAllConnectorsWithApiCalls(projectId).pipe(first()).subscribe();
            }
          },
        ),
        switchMap(({ externalAccountRowId, connectors, projectId, userId }) => {
          let connSelectors$: Observable<ConnectorInfo[] | undefined> =
            this.connectorsApiService.getConnectorsSelector(projectId);

          // For customer portal connector selector, we only fetch connectors associated with
          // the account if externalAccountRowId is present. For non-customer portals, we need
          // to always fetch them.
          if (this.isCustomerPortal() && !!externalAccountRowId) {
            connSelectors$ = this.connectorsApiService.getConnectorsSelector(
              projectId,
              externalAccountRowId,
            );
          } else if (this.isCustomerPortal() && !externalAccountRowId) {
            connSelectors$ = of(undefined);
          }

          return connSelectors$.pipe(
            map((connConnectors) => {
              return { connectors, connConnectors, userId };
            }),
          );
        }),
      )
      .subscribe(({ connectors, connConnectors, userId }) => {
        this.connectionConnectors.set(connConnectors);
        this.updateConnectorInfo(connConnectors ?? connectors, userId);
        this.updateStyles();
        safeDetectChanges(this.cdr);
      });
  }

  /**
   * Initializes computed signals.
   */
  private initSignals(): void {
    this.buttonTooltip = computed(() => {
      if (this.isCustomerPortal() && this.connectorSelector() && !this.connectionConnectors()) {
        return 'In order to connect to a service while in editor, please add accounts dropdown';
      }
      return '';
    });

    this.isButtonDisabled = computed(() => {
      if (this.isCustomerPortal()) {
        return !this.connectorSelector() || !this.connectionConnectors();
      }
      return false;
    });

    this.isGoogleCalendarSelector = computed(
      () =>
        this.connectorSelector()?.configuration?.target === 'GoogleCalendar' ||
        this.connection()?.target === 'GoogleCalendar',
    );

    this.isGoogleMailSelector = computed(
      () =>
        this.connectorSelector()?.configuration?.target === 'GoogleMail' ||
        this.connection()?.target === 'GoogleMail',
    );

    this.isConnected = computed(() => !!this.connectionConnectors() && !!this.connection());

    this.isEditable = computed(
      () =>
        !!this.connection()?.isEditable &&
        !this.isGoogleCalendarSelector() &&
        !this.isGoogleMailSelector(),
    );

    this.label = computed(() => {
      if (this.connectorLabel()) {
        return this.connectorLabel();
      }

      // Connector type has not been confiured, no existing connection found.
      if (!this.connection() && !this.connectorSelector()?.target && !this.isConnected()) {
        return 'Connection Selector';
      }

      // Google Calendar
      if (
        !this.connection() &&
        this.connectorSelector()?.configuration?.target === 'GoogleCalendar'
      ) {
        return 'Connect to Calendar';
      } else if (this.connectorSelector()?.configuration?.target === 'GoogleCalendar') {
        return 'Calendar';
      }

      // GMail
      if (!this.connection() && this.connectorSelector()?.configuration?.target === 'GoogleMail') {
        return 'Connect to Gmail';
      } else if (this.connectorSelector()?.configuration?.target === 'GoogleMail') {
        return 'Gmail';
      }

      // Other connectors
      if (this.connectorSelector()?.name && !this.isConnected()) {
        const target =
          this.connectorSelector()?.configuration?.target || this.connectorSelector()?.target || '';
        return target ? `Connect to ${target}` : this.connectorSelector()?.name || 'Connector';
      } else if (this.connectorSelector()?.target && this.isConnected()) {
        return `Connected to ${this.connection()?.target}`;
      }

      return 'Connection Selector';
    });
  }

  private updateConnectorInfo(connectors: ConnectorInfo[], userId?: string): void {
    const connSelector = connectors.find(({ id }) => id === this.selectorId);
    const isUserScoped =
      connSelector?.target === 'GoogleCalendar' || connSelector?.target === 'GoogleMail';

    // Find connection associated with our connector selector
    const connection = connectors.find((conn) => {
      return (
        conn.target === connSelector?.configuration?.target &&
        // for user-bound connectors match user id.
        (isUserScoped && userId ? conn.userId === userId : true)
      );
    });

    this.connectorSelector.set(connSelector);
    this.connection.set(connection ?? null);
    const connTarget: ConnectorTarget =
      this.connectorSelector()?.configuration?.target ?? this.connectorSelector()?.target;
    if (this.connection()) {
      this.hasError.set(false);
    }
    this.connectorIcon.set(
      connTarget ? connectorServiceIcons[connTarget] : 'ei-connectionSelector',
    );
  }

  private updateStyles(): void {
    const { style } = this.props || {};
    const { button, font, container } = style || {};
    const { primary, secondary } = button || {};
    if (!style || !font || !button) {
      return;
    }

    // Container styles
    if (!container) {
      return;
    }
    const containerStyles: Record<string, string> = {
      color: container.color.color1,
      background: getBackground(container.fill),
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, '700'),
    };
    if (container.outline.selectorType !== 'none') {
      containerStyles['border'] = `1px solid ${container.outline.color1}`;
    }
    if (container.radius !== '0px') {
      containerStyles['border-radius'] = container.radius;
    }
    if (container.width) {
      containerStyles['border-width'] = container.width;
    }
    this.containerStyles = containerStyles;

    // Primary button styles
    if (!primary) {
      return;
    }
    const primaryButtonStyles: Record<string, string> = {
      color: primary.color.color1,
      background: getBackground(primary.fill),
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, '700'),
    };
    if (this.isButtonDisabled()) {
      primaryButtonStyles['opacity'] = '0.4';
    }
    if (primary.outline.selectorType !== 'none') {
      primaryButtonStyles['border'] = `1px solid ${primary.outline.color1}`;
    } else {
      primaryButtonStyles['border'] = `1px solid transparent`;
    }
    if (primary.radius !== '0px') {
      primaryButtonStyles['border-radius'] = primary.radius;
    }
    if (primary.width !== '0px') {
      primaryButtonStyles['border-width'] = primary.width;
    }
    this.primaryButtonStyles = primaryButtonStyles;

    // Secondary button styles
    if (!secondary) {
      return;
    }
    const secondaryButtonStyles: Record<string, string> = {
      color: secondary.color.color1,
      background: getBackground(secondary.fill),
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, '700'),
    };
    if (this.isButtonDisabled()) {
      secondaryButtonStyles['opacity'] = '0.4';
    }
    if (secondary.outline.selectorType !== 'none') {
      secondaryButtonStyles['border'] = `1px solid ${secondary.outline.color1}`;
    } else {
      secondaryButtonStyles['border'] = `1px solid transparent`;
    }
    if (secondary.radius !== '0px') {
      secondaryButtonStyles['border-radius'] = secondary.radius;
    }
    if (secondary.width) {
      secondaryButtonStyles['border-width'] = secondary.width;
    }
    this.secondaryButtonStyles = secondaryButtonStyles;
  }
}
